import * as z from "zod";
import { SalesChannelValues } from "./filters/sales-channel-values";
import { ViewByEnum } from "./filters/view-by-toggle";

/**
 * Schema for claim attachment rate series data
 * This schema defines the data structure for time-series attachment rate analytics
 */

// Input schema for claim attachment rate series data
export const ClaimAttachmentRateSeriesDataInputSchema = z.object({
  startDate: z
    .string()
    .describe("Start date for the analytics period (YYYY-MM-DD)"),
  endDate: z
    .string()
    .describe("End date for the analytics period (YYYY-MM-DD)"),
  viewBy: z
    .enum([
      ViewByEnum.ORDER_DATE,
      ViewByEnum.PROCESSED_DATE,
      ViewByEnum.SUBMITTED_DATE,
    ])
    .default(ViewByEnum.ORDER_DATE)
    .describe(
      "Whether to view analytics by order date, processed date, or submitted date",
    ),
  salesChannels: z
    .array(z.enum(SalesChannelValues))
    .optional()
    .describe("Sales channels to filter by (optional)"),
});

// Data point schema for time-series data
export const ClaimAttachmentRateDataPointSchema = z.object({
  date: z.string().describe("Date for this data point (YYYY-MM-DD)"),
  attachmentRateEligible: z
    .number()
    .describe("Percentage of eligible orders with coverage on this date"),
  attachmentRateTotal: z
    .number()
    .describe("Percentage of total orders with coverage on this date"),
});

// Output schema for claim attachment rate series data
export const ClaimAttachmentRateSeriesDataOutputSchema = z.object({
  data: z
    .array(ClaimAttachmentRateDataPointSchema)
    .describe(
      "Array of data points for day by day breakdown of attachment rates",
    ),
});

// Export type definitions for TypeScript
export type ClaimAttachmentRateSeriesDataInput = z.infer<
  typeof ClaimAttachmentRateSeriesDataInputSchema
>;
export type ClaimAttachmentRateSeriesDataOutput = z.infer<
  typeof ClaimAttachmentRateSeriesDataOutputSchema
>;
export type ClaimAttachmentRateDataPoint = z.infer<
  typeof ClaimAttachmentRateDataPointSchema
>;

// Define the entire schema for RPC usage
export const claimAttachmentRateSeriesData = {
  name: "claimAttachmentRateSeriesData" as const,
  input: ClaimAttachmentRateSeriesDataInputSchema,
  output: ClaimAttachmentRateSeriesDataOutputSchema,
};
